import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '../../services/api';
import { User, UserCreate, Token, AuthState, AuthSuccess, PasswordResetConfirm } from '../../types';

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterCredentials extends UserCreate {}

const storedToken = localStorage.getItem('token');
const storedRefreshToken = localStorage.getItem('refreshToken');

const createTokenObject = (accessToken: string): Token => ({
  access_token: accessToken,
  token_type: 'bearer',
  expires_in: 3600, // Default to 1 hour
  refresh_token: storedRefreshToken || ''
});

const initialState: AuthState = {
  user: null,
  token: storedToken ? createTokenObject(storedToken) : null,
  refreshToken: storedRefreshToken,
  isLoading: false,
  isAuthenticated: !!storedToken,
  passwordResetStatus: null,
  passwordResetRateLimit: null,
  error: null,
};

export const login = createAsyncThunk<AuthSuccess, LoginCredentials>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      // Get the tokens using x-www-form-urlencoded format
      const response = await authApi.login(credentials.email, credentials.password);
      const tokenData = response.data;

      // Get the user details
      const userResponse = await authApi.getCurrentUser();
      const userData = userResponse.data;

      // Check if user role is allowed to login
      const allowedRoles = ['super_admin', 'org_admin'];
      if (!allowedRoles.includes(userData.role)) {
        // Clear any stored tokens since login is not allowed
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        return rejectWithValue('Access denied. Only administrators are allowed to login.');
      }

      // Store tokens in localStorage
      localStorage.setItem('token', tokenData.access_token);
      localStorage.setItem('refreshToken', tokenData.refresh_token);

      return {
        user: userData,
        token: tokenData,
        refresh_token: tokenData.refresh_token
      };
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Login failed');
    }
  }
);

export const register = createAsyncThunk<AuthSuccess, RegisterCredentials>(
  'auth/register',
  async (credentials, { rejectWithValue }) => {
    try {
      // Register the user
      const registerResponse = await authApi.register(credentials);
      const userData = registerResponse.data;

      // Login the user
      const loginResponse = await authApi.login(credentials.email, credentials.password);
      const tokenData = loginResponse.data;

      // Store tokens in localStorage
      localStorage.setItem('token', tokenData.access_token);
      localStorage.setItem('refreshToken', tokenData.refresh_token);

      return {
        user: userData,
        token: tokenData,
        refresh_token: tokenData.refresh_token
      };
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Registration failed');
    }
  }
);

export const requestPasswordReset = createAsyncThunk<string, { email: string }>(
  'auth/requestPasswordReset',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await authApi.requestPasswordReset(email);
      return response.data.message;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        // Check if it's a rate limiting response
        if (typeof error.response.data.detail === 'object' && 
            error.response.data.detail.message && 
            error.response.data.detail.reset_in_seconds) {
          return rejectWithValue({
            type: 'rate_limit',
            data: error.response.data.detail
          });
        }
        
        return rejectWithValue({
          type: 'error',
          message: Array.isArray(error.response.data.detail)
            ? error.response.data.detail[0].msg
            : error.response.data.detail
        });
      }
      return rejectWithValue({
        type: 'error',
        message: 'Failed to request password reset'
      });
    }
  }
);

export const confirmPasswordReset = createAsyncThunk<string, PasswordResetConfirm>(
  'auth/confirmPasswordReset',
  async (request, { rejectWithValue }) => {
    try {
      const response = await authApi.confirmPasswordReset(
        request.token,
        request.password,
        request.password // Use the same password for confirmation
      );
      return response.data.message;
    } catch (error: any) {
      if (error.response?.data?.detail) {
        return rejectWithValue(Array.isArray(error.response.data.detail)
          ? error.response.data.detail[0].msg
          : error.response.data.detail);
      }
      return rejectWithValue('Failed to reset password');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<AuthSuccess>) => {
      const { user, token, refresh_token } = action.payload;
      state.user = user;
      state.token = token;
      state.refreshToken = refresh_token;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;

      // Store tokens in localStorage
      localStorage.setItem('token', token.access_token);
      localStorage.setItem('refreshToken', refresh_token);
    },
    logout: (state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
    },
    clearPasswordResetStatus: (state) => {
      state.passwordResetStatus = null;
      state.passwordResetRateLimit = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refresh_token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        if (action.payload === 'Token has expired') {
          state.user = null;
          state.token = null;
          state.refreshToken = null;
          state.isAuthenticated = false;
        }
      })
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refresh_token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Request Password Reset
      .addCase(requestPasswordReset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.passwordResetStatus = null;
        state.passwordResetRateLimit = null;
      })
      .addCase(requestPasswordReset.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordResetStatus = action.payload;
        state.passwordResetRateLimit = null;
      })
      .addCase(requestPasswordReset.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        
        if (payload?.type === 'rate_limit') {
          state.passwordResetRateLimit = payload.data;
          state.error = null;
        } else {
          state.error = payload?.message || payload;
          state.passwordResetRateLimit = null;
        }
      })
      // Confirm Password Reset
      .addCase(confirmPasswordReset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.passwordResetStatus = null;
      })
      .addCase(confirmPasswordReset.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordResetStatus = action.payload;
      })
      .addCase(confirmPasswordReset.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCredentials, logout, clearPasswordResetStatus } = authSlice.actions;

export default authSlice.reducer;
